// SPDX-License-Identifier: MIT
// Desktop data bridge (step 4) — the persistence seam against local JSON files
// (Rust get/set_projects / _versions / _conversations) instead of Supabase.
// Read-through / write-through, no module cache (single-user desktop app).
//
// Slice 1: project CRUD (list / create / seed / delete).
// Slice 2: version history, discovery answers, backend config, chat re-hydration.
//   The web side does each of these as a Postgres RPC / PATCH / SELECT ; on
//   desktop the bridge composes the local projects.json + versions.json +
//   conversations.json so the open-project card behaves identically offline.
import type { DataBridge, ConversationHistory } from './data-types';
import type { ProjectRow, ProjectVersionRow, VersionContextKind, MessageRow } from '../projects/api';
import { deriveSeedTitle } from '../projects/api';
import type { MagnifyResult } from '../auth/magnify';
import type { Locale } from '../i18n/detect';
import { tauriInvoke } from './tauri';
import { supabase } from '../auth/supabase';
import { appendVersion, listVersionsForProject } from './version-store';
import { conversationIdForProject, getConversation } from './conversation-store';

// Server-side cap the web RPC applies to a version's context snapshot, so a huge
// multimodal/refinement context doesn't bloat the store. Mirror it here.
const MAX_CONTEXT_SNAPSHOT_CHARS = 16_000;

// supabase-js FunctionsHttpError carries the Response in `.context`. Pull the
// status + structured `reason` out so callers (e.g. the card's capacity-vs-
// generic UX) can branch on "429"/"capacity" instead of a generic message.
async function describeFnError(error: { message?: string; context?: unknown }): Promise<string> {
  const ctx = error?.context as { status?: number; clone?: () => Response } | undefined;
  if (ctx && typeof ctx.status === 'number') {
    let detail = String(ctx.status);
    try {
      const body = await (ctx as unknown as Response).clone().json();
      if (body?.reason) detail += ` ${body.reason}`;
      else if (body?.error) detail += ` ${body.error}`;
    } catch { /* body not JSON — status alone is enough */ }
    return detail;
  }
  return error?.message ?? 'unknown error';
}

async function readProjects(): Promise<ProjectRow[]> {
  const raw = await tauriInvoke()('get_projects');
  if (typeof raw !== 'string' || raw.length === 0) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as ProjectRow[]) : [];
  } catch {
    return [];
  }
}

async function writeProjects(rows: ProjectRow[]): Promise<void> {
  await tauriInvoke()('set_projects', { json: JSON.stringify(rows) });
}

// All read-modify-write ops on projects.json funnel through ONE promise chain so
// concurrent callers can't lose updates. On an Atelier load the cloud-sync
// saveProject loop, setLocalDir and saveDiscoveryAnswers all fire together;
// unserialized, each awaited read returned the SAME stale snapshot and the last
// write clobbered the others — a just-saved discovery answer or local_dir silently
// vanished (the 2026-06-02 "réponses disparues" / "projet pas reconnu" dogfood
// regression). Serializing makes every op read the freshest persisted state.
let projectsOpChain: Promise<unknown> = Promise.resolve();
function serializeProjectsOp<T>(op: () => Promise<T>): Promise<T> {
  const run = projectsOpChain.then(op, op);
  // Keep the chain alive regardless of this op's outcome (swallow here only — the
  // real result/rejection still propagates to the caller via `run`).
  projectsOpChain = run.then(() => undefined, () => undefined);
  return run;
}

// Atomic read → mutate (in place) → write of the whole projects array, serialized.
async function mutateProjects<T>(fn: (rows: ProjectRow[]) => T): Promise<T> {
  return serializeProjectsOp(async () => {
    const rows = await readProjects();
    const result = fn(rows);
    await writeProjects(rows);
    return result;
  });
}

// Find + mutate + persist a single project row (the desktop equivalent of a
// Supabase PATCH scoped by id). Throws if the project is gone.
async function updateRow(projectId: string, mutate: (r: ProjectRow) => void): Promise<ProjectRow> {
  return mutateProjects((rows) => {
    const row = rows.find((r) => r.id === projectId);
    if (!row) throw new Error(`[desktop] project ${projectId} not found`);
    mutate(row);
    row.updated_at = new Date().toISOString();
    return row;
  });
}

function makeRow(p: {
  title: string; idea: string; outline: string[]; first_steps: string[];
  stack: string | null; locale: Locale;
}): ProjectRow {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    user_id: 'local',
    title: p.title,
    idea: p.idea,
    outline: p.outline,
    first_steps: p.first_steps,
    stack: p.stack,
    locale: p.locale,
    created_at: now,
    updated_at: now,
    forgejo_repo_url: null,
    forgejo_clone_token: null,
    current_version_number: 1,
    discovery_summary: null,
    discovery_questions: null,
    discovery_answers: null,
    discovery_attachments_seen: null,
    discovery_attachments_skipped: null,
    discovery_run_at: null,
    backend_kind: null,
    backend_url: null,
    backend_anon_key: null,
    deploy_url: null,
    deploy_status: null,
    deployed_at: null,
    deploy_id: null,
    deploy_expires_at: null,
    // Local-only rows are never directly inserted into Supabase by the desktop
    // bridge; they get pushed via cloud-sync with origin='desktop_sync'. Mark
    // them 'web' here so the in-memory shape satisfies ProjectRow and the field
    // has a defined value when rows are read back from the JSON file.
    origin: 'web' as const,
  };
}

function makeVersionRow(
  projectId: string,
  versionNumber: number,
  result: MagnifyResult,
  kind: VersionContextKind,
  contextSnapshot?: string | null,
): ProjectVersionRow {
  return {
    id: crypto.randomUUID(),
    project_id: projectId,
    version_number: versionNumber,
    title: result.title,
    outline: result.outline,
    first_steps: result.first_steps,
    stack: result.stack ?? null,
    context_kind: kind,
    context_snapshot: contextSnapshot ? contextSnapshot.slice(0, MAX_CONTEXT_SNAPSHOT_CHARS) : null,
    created_at: new Date().toISOString(),
  };
}

export const desktopDataBridge: DataBridge = {
  // ── Slice 1 — project CRUD ────────────────────────────────────────────────
  // Stored in creation order ; reverse = newest-first (mirrors the web
  // created_at DESC ordering, deterministically without timestamp granularity).
  async listProjects() {
    // Through the chain so a list reflects the latest committed write (not a
    // snapshot mid-way through a concurrent cloud-sync push).
    return serializeProjectsOp(async () => (await readProjects()).slice().reverse());
  },
  async saveProject(result, idea, locale) {
    const row = makeRow({
      title: result.title, idea, outline: result.outline,
      first_steps: result.first_steps, stack: result.stack ?? null, locale,
    });
    await mutateProjects((rows) => { rows.push(row); });
    // Seed v1 — the web DB trigger inserts v1 on the initial INSERT, so the
    // version chip + prev-nav have a baseline to walk back to.
    await appendVersion(makeVersionRow(row.id, 1, result, 'initial', null));
    return row;
  },
  async saveProjectSeed(idea, locale) {
    const row = makeRow({ title: deriveSeedTitle(idea), idea, outline: [], first_steps: [], stack: null, locale });
    await mutateProjects((rows) => { rows.push(row); });
    await appendVersion(
      makeVersionRow(row.id, 1, { title: row.title, outline: [], first_steps: [], stack: undefined }, 'initial', null),
    );
    return row;
  },
  async deleteProject(id) {
    await mutateProjects((rows) => {
      for (let i = rows.length - 1; i >= 0; i--) if (rows[i].id === id) rows.splice(i, 1);
    });
  },

  // ── Slice 2 — version history ─────────────────────────────────────────────
  async saveProjectVersion(projectId, result, kind, contextSnapshot) {
    const newVersion = await mutateProjects((rows) => {
      const row = rows.find((r) => r.id === projectId);
      if (!row) throw new Error(`[desktop] saveProjectVersion: project ${projectId} not found`);
      const v = row.current_version_number + 1;
      // The displayed projects.* fields always mirror the current version's
      // content (single source of truth for "what to show") — same invariant as web.
      row.title = result.title;
      row.outline = result.outline;
      row.first_steps = result.first_steps;
      row.stack = result.stack ?? null;
      row.current_version_number = v;
      row.updated_at = new Date().toISOString();
      return v;
    });
    await appendVersion(makeVersionRow(projectId, newVersion, result, kind, contextSnapshot));
    return newVersion;
  },
  listProjectVersions(projectId) {
    return listVersionsForProject(projectId);
  },
  async switchToProjectVersion(projectId, versionNumber) {
    const target = (await listVersionsForProject(projectId)).find((v) => v.version_number === versionNumber);
    if (!target) {
      throw new Error(`[desktop] switchToProjectVersion: v${versionNumber} not found for ${projectId}`);
    }
    await updateRow(projectId, (row) => {
      row.title = target.title;
      row.outline = target.outline;
      row.first_steps = target.first_steps;
      row.stack = target.stack;
      row.current_version_number = versionNumber;
    });
    return versionNumber;
  },

  // ── Slice 2 — discovery answers + backend config ──────────────────────────
  // Persist the discovery snapshot locally — the web edge fn writes these columns
  // server-side, but the desktop discover() bridge only returns them. Without this
  // the summary/questions never reach projects.json and hasStoredDiscovery() is false
  // on reload, orphaning the saved answers (Bug B). Stamp run_at like the edge fn.
  // Leaves discovery_answers untouched (saved separately by saveDiscoveryAnswers).
  async saveDiscovery(projectId, discovery) {
    await updateRow(projectId, (row) => {
      row.discovery_summary = discovery.discovery_summary;
      row.discovery_questions = discovery.questions;
      row.discovery_attachments_seen = discovery.attachments_seen;
      row.discovery_attachments_skipped = discovery.attachments_skipped;
      row.discovery_run_at = new Date().toISOString();
    });
  },
  async saveDiscoveryAnswers(projectId, answers) {
    await updateRow(projectId, (row) => { row.discovery_answers = answers; });
  },
  async saveProjectBackend(projectId, url, anonKey) {
    await updateRow(projectId, (row) => {
      row.backend_kind = 'byo-supabase';
      row.backend_url = url;
      row.backend_anon_key = anonKey;
    });
  },
  async clearProjectBackend(projectId) {
    await updateRow(projectId, (row) => {
      row.backend_kind = null;
      row.backend_url = null;
      row.backend_anon_key = null;
    });
  },
  async provisionBackend(projectId, opts) {
    const body: Record<string, unknown> = { project_id: projectId };
    if (opts?.handle) body.slug = opts.handle;
    if (opts?.model) body.model = opts.model;
    const { data, error } = await supabase.functions.invoke('provision-backend', {
      body,
    });
    if (error) {
      throw new Error(`[desktop] provision-backend failed: ${await describeFnError(error)}`);
    }
    if (!data?.backend_url || !data?.backend_anon_key) {
      throw new Error('[desktop] provision-backend failed: no creds returned');
    }
    const url = data.backend_url as string;
    const anonKey = data.backend_anon_key as string;
    const kind = opts?.model === 'full-supabase' ? 'full-supabase' : 'genesis-rest';
    await updateRow(projectId, (row) => {
      row.backend_kind = kind;
      row.backend_url = url;
      row.backend_anon_key = anonKey;
    });
    return {
      backendUrl: url,
      backendAnonKey: anonKey,
      expiresAt: (data.expires_at as string | null) ?? null,
      alreadyProvisioned: Boolean(data.already_provisioned),
    };
  },

  // ── deploy_frontend slice (2026-06-09) ────────────────────────────────────
  // Deploy the generated frontend via the JWT-authed deploy-frontend edge fn (the
  // GEN_DEPLOYER_TOKEN stays server-side). On success the deploy state is mirrored
  // into the local row so the open-project card shows the live URL offline too.
  // Throws on no-repo (422)/capacity/deployer failure so the UI never shows a
  // half-written state.
  async deployFrontend(projectId, opts) {
    const { data, error } = await supabase.functions.invoke('deploy-frontend', {
      body: { project_id: projectId, handle: opts?.handle, redeploy: opts?.redeploy },
    });
    if (error) {
      throw new Error(`[desktop] deploy-frontend failed: ${await describeFnError(error)}`);
    }
    if (!data?.deploy_url) {
      throw new Error('[desktop] deploy-frontend failed: no deploy_url');
    }
    const url = data.deploy_url as string;
    const deployId = (data.deploy_id as string | null) ?? null;
    const status = (data.status as 'building' | 'live' | 'failed') ?? 'building';
    await updateRow(projectId, (row) => {
      row.deploy_url = url;
      row.deploy_status = status;
      row.deploy_id = deployId;
    });
    return { deployUrl: url, deployId, status };
  },
  async getDeployStatus(projectId) {
    const { data, error } = await supabase.functions.invoke('deploy-frontend', {
      body: { project_id: projectId, status_only: true },
    });
    if (error) {
      throw new Error(`[desktop] deploy-status failed: ${await describeFnError(error)}`);
    }
    // null (never deployed / torn_down) passes through unchanged — do NOT default to
    // 'building', that would lie about a project that was never (or no longer) deployed.
    const status = (data?.status as 'building' | 'live' | 'failed' | null) ?? null;
    const deployUrl = (data?.deploy_url as string | null) ?? null;
    // Refresh the local row so a reopened card reflects the latest state offline.
    await updateRow(projectId, (row) => {
      row.deploy_status = status;
      if (deployUrl) row.deploy_url = deployUrl;
    }).catch(() => { /* row may be gone — status is still returned */ });
    return { status, deployUrl };
  },

  // ── Slice 2 — chat history re-hydration ───────────────────────────────────
  // The conversation-store records {role, content} + (on assistant turns) the
  // manifest_changed / version_number badge metadata, so the re-hydrated
  // MessageRows carry the "✓ fiche → vN" badge across an app restart — same
  // fidelity as the web messages table. Synthesize stable ids + empty timestamp.
  async loadConversationHistory(projectId): Promise<ConversationHistory | null> {
    const conversationId = conversationIdForProject(projectId);
    const stored = await getConversation(conversationId);
    if (stored.length === 0) return null;
    const messages: MessageRow[] = stored.map((m, i) => ({
      id: `${conversationId}-${i}`,
      conversation_id: conversationId,
      role: m.role,
      content: m.content,
      manifest_changed: m.manifest_changed ?? false,
      version_number: m.version_number ?? null,
      created_at: '',
    }));
    return { conversationId, messages };
  },
};

// cloud-sync v2 helper: copy a cloud project's DISCOVERY snapshot onto the matching
// local row — the attachment ANALYSIS (discovery_summary + which files were seen) and
// the QUESTIONS asked. projectRowToMagnifyResult drops these, so a freshly-pulled
// desktop project would otherwise show an empty discovery panel vs the web. Idempotent
// (a plain field copy). Standalone (not on the DataBridge interface) since only the
// desktop pull needs it.
// Remember where a project was delivered on disk, so a re-open doesn't re-offer
// "create the folder" (and create a 3rd duplicate) and the agent knows the path.
export async function setLocalDir(projectId: string, dir: string): Promise<void> {
  await updateRow(projectId, (row) => { row.local_dir = dir; });
}

// Apply a partial field update to a project row AND record its local_dir, in one
// atomic read-modify-write (used by the continuity hydration after a resume clone).
export async function upsertProjectFields(
  projectId: string,
  fields: Partial<ProjectRow>,
  localDir: string,
): Promise<void> {
  await updateRow(projectId, (row) => {
    Object.assign(row, fields);
    row.local_dir = localDir;
  });
}

export async function applyCloudDiscovery(projectId: string, cloud: ProjectRow): Promise<void> {
  await updateRow(projectId, (row) => {
    // FILL-MISSING, never clobber. The user answers discovery questions LOCALLY on
    // desktop (discovery_answers) — those edits are authoritative and must survive a
    // re-sync. So keep any local value and only take the cloud's when local is empty.
    // (A 2026-06-02 regression overwrote saved answers with the cloud's null on every
    // Atelier load — exactly the persistence v3 was meant to guarantee.)
    row.discovery_summary = row.discovery_summary ?? cloud.discovery_summary;
    row.discovery_questions = row.discovery_questions ?? cloud.discovery_questions;
    row.discovery_answers = row.discovery_answers ?? cloud.discovery_answers;
    row.discovery_attachments_seen = row.discovery_attachments_seen ?? cloud.discovery_attachments_seen;
    row.discovery_attachments_skipped = row.discovery_attachments_skipped ?? cloud.discovery_attachments_skipped;
    row.discovery_run_at = row.discovery_run_at ?? cloud.discovery_run_at;
  });
}

// Cloud-sync v2 — pull a project's backend creds web→desktop. FILL-MISSING: only
// when the local trio is empty AND the cloud trio is complete (the DB completeness
// CHECK makes creds all-or-nothing). Kind-preserving — unlike saveProjectBackend
// which hardcodes 'byo-supabase', this keeps the cloud's real kind (genesis-rest /
// full-supabase / byo-supabase). Returns true iff it filled (so the caller knows to
// write the project's .env.local). Standalone (desktop pull only), like applyCloudDiscovery.
export async function applyCloudBackend(projectId: string, cloud: ProjectRow): Promise<boolean> {
  const cloudComplete = Boolean(cloud.backend_kind && cloud.backend_url && cloud.backend_anon_key);
  if (!cloudComplete) return false;
  let filled = false;
  await updateRow(projectId, (row) => {
    const localEmpty = !row.backend_kind && !row.backend_url && !row.backend_anon_key;
    if (!localEmpty) return; // never clobber a populated local trio
    row.backend_kind = cloud.backend_kind;
    row.backend_url = cloud.backend_url;
    row.backend_anon_key = cloud.backend_anon_key;
    filled = true;
  });
  return filled;
}

// Cloud-sync v2 — pull cloud forgejo_repo_url into a local project that has none
// (fill-missing). Never clobbers a populated local value. Mirror of applyCloudBackend.
export async function applyCloudForgejoUrl(projectId: string, cloud: ProjectRow): Promise<boolean> {
  if (!cloud.forgejo_repo_url) return false;
  let filled = false;
  await updateRow(projectId, (row) => {
    if (row.forgejo_repo_url) return; // never clobber a populated local value
    row.forgejo_repo_url = cloud.forgejo_repo_url;
    filled = true;
  });
  return filled;
}

// Cloud-sync v2 — pull a project's deploy state web→desktop. FILL-MISSING per field:
// keep any local value, take the cloud's only where local is empty. (Deploy executes
// on the cloud/web side via the gen-deployer broker; desktop can't deploy yet, so this
// is web→desktop only. Inert until deploy_frontend is activated — cloud deploy_* stay
// null until then.) Standalone, like applyCloudDiscovery.
export async function applyCloudDeploy(projectId: string, cloud: ProjectRow): Promise<void> {
  await updateRow(projectId, (row) => {
    row.deploy_url = row.deploy_url ?? cloud.deploy_url;
    row.deploy_status = row.deploy_status ?? cloud.deploy_status;
    row.deploy_id = row.deploy_id ?? cloud.deploy_id;
    row.deployed_at = row.deployed_at ?? cloud.deployed_at;
  });
}
